import $ from 'jquery';

/**
 * This is a general class that can be extended.
 * It provides functions that connects the front end to the SVT API on the server.
 */

class SVTAPI {
  constructor(endpoint) {
    this._baseURL = '/api/1.0.0' + endpoint;
  }

  /**
   * Returns the url where the AJAX request is made.
   *
   * @param   {string} suffix A string to put at the end of the base URL
   *
   * @returns {string}        The full url to use in the AJAX request.
   */
  getURL(suffix) {
    return this._addSlash(this._baseURL + suffix);
  }

  /**
   * Ensures the URL has a single trailing slash
   * This prevents unnecessary redirects caused by Stage. ('items/4' redirects to 'items/4/')
   *
   * @param   {string}  url The url to add slash to
   * @returns {string}      The url with the slash
   */
  _addSlash(url) {
    return (url + '/').replace(/(\/)+$/, '/');
  }

  /**
   * Makes a GET request to the API.
   *
   * @param   {string} url  The part after the base url where the AJAX request is made
   * @param   {Object} data The data to send with the AJAX request
   *
   * @returns {Promise}     The response data that the server sent
   */
  get({ url = '', data = {} }) {
    return this._ajax({ url, data });
  }

  /**
   * Makes a POST request to the API.
   *
   * @param   {string} url  The part after the base url where the AJAX request is made
   * @param   {Object} data The data to send with the AJAX request
   *
   * @returns {Promise}     The response data that the server sent
   */
  post({ url = '', data = {} }) {
    return this._ajax({ url, data, type: 'POST' });
  }

  /**
   * Makes a PATCH request to the API.
   *
   * @param {string}  url   - The part after the base url where the AJAX request is made
   * @param {Object}  data  - The data to send with the AJAX request
   * @param {boolean} async - Asynchronous or synchronous request. Default is true.
   *
   * @returns {Promise} - The response data that the server sent
   */
  patch({ url = '', data = {}, async = true }) {
    return this._ajax({ url, data, type: 'PATCH', async });
  }

  /**
   * Makes a DELETE request to the API.
   *
   * @param {string} url - The part after the base url where the AJAX request is made
   * @param {Object} data  - The data to send with the AJAX request
   *
   * @returns {Promise} - The response data that the server sent
   */
  remove({ url = '', data = {} }) {
    return this._ajax({ url, type: 'DELETE', data });
  }

  /**
   * Makes an AJAX request to the API.
   *
   * @param   {string}  url   - The part after the base url where the AJAX request is made
   * @param   {Object}  data  - The data to send with the AJAX request
   * @param   {string}  type  - The request method
   * @param   {boolean} async - Asynchronous or synchronous request. Default is true.
   *
   * @returns {Promise} - The response data that the server sent
   */
  _ajax({ url = '', data = {}, type = 'GET', async = true }) {
    return this._parseResult($.ajax({
      url: this.getURL(url),
      type,
      dataType: 'json',
      contentType: 'application/json',
      data: type === 'GET' ? data : JSON.stringify(data),
      async
    }));
  }

  /**
   * Parses the response data from the AJAX response.
   *
   * @param   {JQueryXHR} jqxhr The jqXHR object that $.ajax returned
   * @returns {Promise}         The response data that the server sent
   */
  _parseResult(jqxhr) {
    return new Promise(resolve => {
      try {
        jqxhr.done((data, textStatus, jqxhr) => {
          resolve(data);
        }).fail((jqxhr, textStatus, errorThrown) => {
          resolve(jqxhr.responseJSON);
        });
      } catch (error) {
        resolve(jqxhr.responseJSON);
      }
    });
  }
}

export default SVTAPI;
